import { useDialog } from '@dni-hooks/useDialog'
import { useOrder } from '@hooks-api/ec/use-order'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { OrderDetailProvider, type OrderDetailProviderProps } from '@order-detail/components/shared/OrderDetailProvider/OrderDetailProvider'
import { OrderDetail } from '@order-detail/components/shared/Orders/OrderDetail'
import { SkeletonOrderDetail } from '@order-detail/components/shared/Orders/SkeletonOrderDetail'
import { useHistory, useParams } from 'react-router-dom-v5'
import { useStmCampaignCode } from '../../hooks'
import { LayoutMyAccount } from '../components/LayoutMyAccount'
import {
  DIALOG_CONFIRM_CANCEL_TRANSACTION,
  DialogConfirmCancelTransaction,
  type DialogConfirmCancelTransactionProps,
} from '../orders/components/DialogConfirmCancelTransaction'

interface OrderDetailContainerProps {
  basePath: string
  onLogout: () => void
}

export function OrderDetailContainer({ basePath, onLogout }: OrderDetailContainerProps) {
  const { transactionId } = useParams<{ transactionId: string }>()
  const { campaignCode, countryCode } = useStmCampaignCode()
  const { order: { destructuredData: transaction, isLoading: isLoadingOrder } } = useOrder({ transactionId, countryCode: countryCode ?? undefined })
  const history = useHistory()
  const { openDialog: openConfirmCancelTransactionDialog } = useDialog<DialogConfirmCancelTransactionProps>(DIALOG_CONFIRM_CANCEL_TRANSACTION)
  const canCancelTransaction = transaction?.status === FastpassOrderStatus.PENDING
  const canPaymentTransaction = transaction?.status === FastpassOrderStatus.PENDING

  const retryPaymentUrl = () => {
    history.push(`${basePath}/retry-payment/${transactionId}`)
  }

  const handleCancelTransaction = () => {
    if (!transactionId) {
      console.error('[OrderSummary] no transactionId')
      return
    }
    openConfirmCancelTransactionDialog({ transactionId, basePath })
  }

  const countryCodeFromOrder = transaction?.country_code
  const isWarehouseOrSubscription = !!transaction?.child_orders[0]?.subship_info?.type
  const orderDetailContextValue: OrderDetailProviderProps = {
    transaction,
    currency: transaction?.currency,
    showRecipientInfo: true,
    countryCode: countryCodeFromOrder,
    onPayment: canPaymentTransaction ? retryPaymentUrl : undefined,
    onCancel: canCancelTransaction ? handleCancelTransaction : undefined,
    showPdf: transaction?.need_pdf,
    isWarehouseOrSubscription,
    environment: 'merchant',
    b2bProductImageFirst: true,
  }

  return (
    <>
      <OrderDetailProvider {...orderDetailContextValue}>
        <LayoutMyAccount campaignCode={campaignCode} basePath={basePath} onLogout={onLogout}>
          {isLoadingOrder
            ? <SkeletonOrderDetail />
            : <OrderDetail />}
        </LayoutMyAccount>
      </OrderDetailProvider>
      <DialogConfirmCancelTransaction />
    </>
  )
}
